import axios from 'axios';
import config from 'config';
import { getAuth } from 'firebase/auth';

const apiBase = config.apiUrl;

export async function orchestrateAgent({ content, messages = [] }) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/agent/orchestrate`;
  const body = { content, messages };

  const res = await axios.post(url, body, {
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`
    },
    withCredentials: true
  });

  return res.data;
}

export async function startAgentStream({ content, messages = [] }) {
  // Return a config object instead of URL since we need POST
  return { content, messages };
}

export async function openAgentStream(config, { onOpen, onToken, onComplete, onError } = {}) {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) throw new Error('Not authenticated');
  const token = await user.getIdToken();

  const url = `${apiBase}/agentic/agent/orchestrate/stream`;

  let lastTextFragment = '';

  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`
      },
      credentials: 'include',
      body: JSON.stringify({ content: config.content, messages: config.messages })
    });

    onOpen && onOpen();

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    let currentEventType = null;

    const processText = (text) => {
      buffer += text;
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('event:')) {
          currentEventType = line.substring(6).trim();
        } else if (line.startsWith('data:')) {
          const data = line.substring(5).trim();
          try {
            const parsed = JSON.parse(data);
            // Skip emitting the final event as a token - it's the complete message we already have
            if (currentEventType === 'final') {
              // Just update lastTextFragment to the final message if needed, but don't emit as token
              const finalText = parsed?.message || parsed?.text || '';
              if (finalText && finalText.length > lastTextFragment.length) {
                lastTextFragment = finalText;
              }
            } else {
              // This is a token event - emit it
              const norm = normalizeToken(parsed);
              if (norm?.text) {
                lastTextFragment += norm.text;
                onToken && onToken(norm);
              }
            }
          } catch (e) {
            // Ignore parse errors
          }
          currentEventType = null; // Reset after processing data
        }
      }
    };

    // eslint-disable-next-line no-constant-condition
    while (true) {
      const { done, value } = await reader.read();
      if (done) {
        onComplete && onComplete({ lastText: lastTextFragment });
        break;
      }
      const text = decoder.decode(value, { stream: true });
      processText(text);
    }

    return { close: () => reader.cancel() };
  } catch (err) {
    console.error('[agentStream] Error:', err);
    onError && onError(err);
    return { close: () => {} };
  }
}

// Keep old EventSource-based handler for backward compatibility
export function openAgentStreamOld(streamUrl, { onOpen, onToken, onComplete, onError } = {}) {
  console.log('[agentStream] STARTING with streamUrl:', streamUrl);
  const absoluteUrl = streamUrl.startsWith('http')
    ? streamUrl
    : `${apiBase}${streamUrl.startsWith('/') ? '' : '/'}${streamUrl}`;
  console.log('[agentStream] absoluteUrl:', absoluteUrl);

  const es = new EventSource(absoluteUrl, { withCredentials: true });
  console.log('[agentStream] EventSource created:', es);
  let lastTextFragment = '';

  es.addEventListener('open', (e) => {
    console.log('[agentStream] open', e);
    onOpen && onOpen(e);
  });

  // Some backends emit incremental tokens as 'token'
  es.addEventListener('token', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] token', data);
      const norm = normalizeToken(data);
      if (norm?.text) lastTextFragment += norm.text;
      onToken && onToken(norm);
    } catch (err) {
      // ignore malformed tokens
    }
  });

  // Handle agent-specific events: plan, planner_meta, final
  es.addEventListener('plan', (e) => {
    // Optional: surface planning as a thought token if desired
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] plan', data);
      const text = typeof data === 'string' ? data : JSON.stringify(data);
      onToken && onToken({ text, type: 'thought' });
    } catch (err) {
      // ignore
    }
  });

  es.addEventListener('planner_meta', () => {
    console.log('[agentStream] planner_meta');
    // Ignore metadata for now
  });

  es.addEventListener('final', (e) => {
    // Emit the final message as a token, then complete
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] final', data);
      const text = (typeof data === 'string') ? data : (data?.message || data?.text || data?.content || '');
      if (text) {
        lastTextFragment += text;
        onToken && onToken({ text });
      }
    } catch (err) {
      // ignore
    }
    onComplete && onComplete({ lastText: lastTextFragment });
    es.close();
  });

  // Generic message fallback
  es.addEventListener('message', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] message', data);
      const norm = normalizeToken(data);
      if (norm?.text) lastTextFragment += norm.text;
      if (norm?.text) onToken && onToken(norm);
    } catch (err) {
      // ignore
    }
  });

  // Tool events: show as thought or ignore
  es.addEventListener('tool', (e) => {
    try {
      const data = safeParse(e.data);
      console.log('[agentStream] tool', data);
      const summary = typeof data === 'string' ? data : (data?.output?.data?.summary || JSON.stringify(data));
      if (summary) onToken && onToken({ text: summary, type: 'thought' });
    } catch (err) {
      // ignore
    }
  });

  es.addEventListener('complete', () => {
    console.log('[agentStream] complete', { lastText: lastTextFragment });
    onComplete && onComplete({ lastText: lastTextFragment });
    es.close();
  });

  es.addEventListener('error', (e) => {
    console.error('[agentStream] error', e);
    onError && onError(e);
    es.close();
  });

  return es;
}

// Helpers
function safeParse(str) {
  if (!str) return {};
  try {
    return JSON.parse(str);
  } catch {
    return str;
  }
}

function normalizeToken(data) {
  if (data == null) return { text: '' };
  if (typeof data === 'string') return { text: data };
  const text = data.text || data.delta || data.content || data.message || '';
  const type = data.type || data.channel || data.kind;
  return { text, type };
}


